import React, { useState, useEffect, useCallback } from 'react';
import {
  FolderOpen, FolderClosed, FileText, Download, Eye, Upload,
  ChevronRight, ChevronDown, Search, RefreshCw, Clock, User,
  CheckCircle, AlertCircle, Plus, X, History, Filter, FileUp, Send, Loader2
} from 'lucide-react';
import axios from '../api/client';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const BMSAdministration = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedSubFolder, setSelectedSubFolder] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [revisionHistory, setRevisionHistory] = useState([]);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // BMS Department folders
  const departments = [
    { id: '0100', name: 'Sales and Estimating' },
    { id: '0200', name: 'Project Management' },
    { id: '0300', name: 'Supply Chain' },
    { id: '0400', name: 'Engineering' },
    { id: '0500', name: 'Manufacturing' },
    { id: '0600', name: 'Quality Control' },
    { id: '0700', name: 'Aftermarket' },
    { id: '0800', name: 'Accounts' },
    { id: '0900', name: 'BMS Administration' },
    { id: '1000', name: 'Human Resources' }
  ];

  // Sub-folder categories
  const subFolders = [
    { id: 'procedures', name: 'Procedures', icon: FileText },
    { id: 'lwis', name: "LWI's", icon: FileText },
    { id: 'directive', name: 'Directive', icon: FileText },
    { id: 'forms', name: 'Forms', icon: FileText },
    { id: 'standards', name: 'Standards', icon: FileText }
  ];

  const [uploadData, setUploadData] = useState({
    file: null,
    document_name: '',
    department: '',
    document_category: 'procedures',
    description: '',
    version: '1.0',
    effective_date: new Date().toISOString().split('T')[0],
    bypass_approval: false
  });

  // Bulk upload state
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkUploadDept, setBulkUploadDept] = useState('');
  const [bulkUploadCategory, setBulkUploadCategory] = useState('procedures');
  const [bulkUploading, setBulkUploading] = useState(false);
  const [deptStats, setDeptStats] = useState([]);
  const [qaManagerEmail, setQaManagerEmail] = useState(null);

  // Check if current user is the QA Manager (who can bypass approval)
  const isQAManager = user?.email === qaManagerEmail || user?.role === 'qa_manager';
  const isDeptManager = user?.role === 'manager' || user?.is_department_manager || user?.role === 'admin';
  const canBypassApproval = isQAManager;
  const canDownloadClean = isQAManager || isDeptManager; // QA Managers and Dept Managers get clean copies

  // State for document submission modal
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitData, setSubmitData] = useState({
    document_name: '',
    document_type: 'procedure',
    department: '',
    description: '',
    file: null
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchDocuments();
    fetchDeptStats();
    fetchQAManagerEmail();
  }, []);

  const fetchQAManagerEmail = async () => {
    try {
      const response = await axios.get('/email-settings');
      if (response.data?.settings?.qa_manager_email) {
        setQaManagerEmail(response.data.settings.qa_manager_email);
      }
    } catch (error) {
      console.error('Error fetching QA Manager email:', error);
    }
  };

  const fetchDeptStats = async () => {
    try {
      const response = await axios.get('/bms-documents/stats');
      setDeptStats(response.data);
    } catch (error) {
      console.error('Error fetching dept stats:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      // Fetch approved documents and directly uploaded BMS documents
      const response = await axios.get('/bms-documents');
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching BMS documents:', error);
      // Fallback to regular documents that are approved
      try {
        const fallback = await axios.get('/documents');
        const approvedDocs = fallback.data.filter(d => 
          d.status === 'Approved' || d.bms_direct_upload === true
        );
        setDocuments(approvedDocs);
      } catch (e) {
        setDocuments([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchRevisionHistory = async (documentId) => {
    try {
      const response = await axios.get(`/documents/${documentId}/audit-trail`);
      setRevisionHistory(response.data);
    } catch (error) {
      console.error('Error fetching revision history:', error);
      setRevisionHistory([]);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`/documents/${doc.id}/download`, {
        responseType: 'blob'
      });
      
      // Check if the response is an error (JSON instead of blob)
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const error = JSON.parse(text);
        toast.error(error.detail || 'File not available for download');
        return;
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename || doc.document_name || 'document');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      // Show info about watermark if applicable
      if (!canDownloadClean) {
        toast.info('Document downloaded with "UNCONTROLLED" watermark');
      } else {
        toast.success('Document downloaded successfully');
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      if (error.response?.status === 404) {
        toast.error('File not found. The physical file may not be available.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to download document');
      }
    }
  };

  // Submit document to Document Control workflow
  const handleSubmitDocument = async () => {
    if (!submitData.document_name || !submitData.department || !submitData.file) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      setSubmitting(true);
      
      // First create the document
      const formData = new FormData();
      formData.append('file', submitData.file);
      formData.append('document_name', submitData.document_name);
      formData.append('document_type', submitData.document_type);
      formData.append('department', submitData.department);
      formData.append('department_code', submitData.department);
      formData.append('description', submitData.description);
      
      const createResponse = await axios.post('/documents/upload-for-approval', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (createResponse.data?.id) {
        // Submit for approval
        await axios.post(`/documents/${createResponse.data.id}/submit-for-approval`);
        toast.success('Document submitted for approval! It will appear here once approved.');
        setShowSubmitModal(false);
        setSubmitData({
          document_name: '',
          document_type: 'procedure',
          department: '',
          description: '',
          file: null
        });
      }
    } catch (error) {
      console.error('Error submitting document:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit document');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDirectUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file || !uploadData.document_name || !uploadData.department) {
      alert('Please fill in all required fields');
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadData.file);
    formData.append('document_name', uploadData.document_name);
    formData.append('department', uploadData.department);
    formData.append('document_category', uploadData.document_category);
    formData.append('description', uploadData.description);
    formData.append('version', uploadData.version);
    formData.append('effective_date', uploadData.effective_date);
    formData.append('bypass_approval', 'true');
    formData.append('bms_direct_upload', 'true');

    try {
      await axios.post('/documents/bms-direct-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert('Document uploaded directly to BMS Administration!');
      setShowUploadModal(false);
      resetUploadForm();
      fetchDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      alert(error.response?.data?.detail || 'Failed to upload document');
    }
  };

  const resetUploadForm = () => {
    setUploadData({
      file: null,
      document_name: '',
      department: '',
      document_category: 'procedures',
      description: '',
      version: '1.0',
      effective_date: new Date().toISOString().split('T')[0],
      bypass_approval: false
    });
  };

  // Bulk upload handler
  const handleBulkUpload = async (e) => {
    e.preventDefault();
    if (bulkFiles.length === 0 || !bulkUploadDept) {
      alert('Please select files and department');
      return;
    }

    setBulkUploading(true);
    const formData = new FormData();
    
    bulkFiles.forEach(file => {
      formData.append('files', file);
    });
    formData.append('department', bulkUploadDept);
    formData.append('document_category', bulkUploadCategory);
    formData.append('bypass_approval', 'true');

    try {
      const response = await axios.post('/bms-documents/bulk-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      alert(`Successfully uploaded ${response.data.uploaded_count} documents!${response.data.error_count > 0 ? ` (${response.data.error_count} errors)` : ''}`);
      setShowBulkUploadModal(false);
      setBulkFiles([]);
      setBulkUploadDept('');
      setBulkUploadCategory('procedures');
      fetchDocuments();
      fetchDeptStats();
    } catch (error) {
      console.error('Error bulk uploading:', error);
      alert(error.response?.data?.detail || 'Failed to upload documents');
    } finally {
      setBulkUploading(false);
    }
  };

  const toggleFolder = (deptId) => {
    setExpandedFolders(prev => ({
      ...prev,
      [deptId]: !prev[deptId]
    }));
  };

  const selectFolder = (dept, subFolder = null) => {
    setSelectedFolder(dept);
    setSelectedSubFolder(subFolder);
  };

  const getPreviewUrl = (doc) => {
    if (!doc?.id) return null;
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${baseUrl}/api/documents/${doc.id}/preview`;
  };

  const isPreviewable = (doc) => {
    // PDF and DOCX files can be previewed (DOCX is converted to PDF on-the-fly)
    const name = (doc?.original_filename || doc?.document_name || '').toLowerCase();
    const hasFile = doc?.file_path && doc.file_path.length > 0;
    return hasFile && (name.endsWith('.pdf') || name.endsWith('.docx'));
  };

  const openPreview = async (doc) => {
    setSelectedDocument(doc);
    setShowPreviewModal(true);
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewBlobUrl(null);
    
    try {
      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${baseUrl}/api/documents/${doc.id}/preview`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to load preview';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.detail || errorMessage;
        } catch (e) {
          // Not JSON response
        }
        throw new Error(errorMessage);
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setPreviewBlobUrl(url);
    } catch (error) {
      console.error('Preview error:', error);
      setPreviewError(error.message || 'Failed to load document preview');
    } finally {
      setPreviewLoading(false);
    }
  };
  
  const closePreview = () => {
    setShowPreviewModal(false);
    // Clean up blob URL to prevent memory leaks
    if (previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
    }
    setPreviewError(null);
  };

  const openRevisionHistory = async (doc) => {
    setSelectedDocument(doc);
    await fetchRevisionHistory(doc.id);
    setShowRevisionModal(true);
  };

  // Filter documents based on selected folder
  const getFilteredDocuments = useCallback(() => {
    let filtered = documents;

    // Filter by department
    if (selectedFolder) {
      filtered = filtered.filter(doc => 
        doc.department === selectedFolder.name || 
        doc.department === selectedFolder.id ||
        doc.bms_department === selectedFolder.id
      );
    }

    // Filter by sub-folder/category
    if (selectedSubFolder) {
      filtered = filtered.filter(doc => 
        doc.document_category === selectedSubFolder.id ||
        doc.document_type?.toLowerCase() === selectedSubFolder.name.toLowerCase()
      );
    }

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(doc =>
        doc.document_name?.toLowerCase().includes(search) ||
        doc.description?.toLowerCase().includes(search)
      );
    }

    return filtered;
  }, [documents, selectedFolder, selectedSubFolder, searchTerm]);

  const filteredDocuments = getFilteredDocuments();

  // Count documents per folder
  const getDocumentCount = (deptId, subFolderId = null) => {
    return documents.filter(doc => {
      const matchesDept = doc.department === departments.find(d => d.id === deptId)?.name ||
                         doc.bms_department === deptId;
      if (!matchesDept) return false;
      if (subFolderId) {
        return doc.document_category === subFolderId;
      }
      return true;
    }).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="bms-administration-page" className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center">
            <FolderOpen className="mr-3 text-blue-600" size={32} />
            BMS Administration
          </h1>
          <p className="text-slate-600 mt-2">
            Business Management System Document Repository - Organized by Department & Category
          </p>
        </div>
        <div className="flex gap-2">
          {/* Submit Document button - visible to all users */}
          <button
            onClick={() => setShowSubmitModal(true)}
            className="flex items-center px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
            data-testid="submit-document-btn"
          >
            <FileUp size={18} className="mr-2" />
            Submit Document
          </button>
          {canBypassApproval && (
            <>
              <button
                onClick={() => setShowBulkUploadModal(true)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                data-testid="bulk-upload-btn"
              >
                <Upload size={18} className="mr-2" />
                Bulk Upload
              </button>
              <button
                onClick={() => setShowUploadModal(true)}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                data-testid="direct-upload-btn"
              >
                <Plus size={18} className="mr-2" />
                Single Upload
              </button>
            </>
          )}
        </div>
      </div>

      {/* Info Banner - Role-specific messages */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-blue-900 mb-1">Document Organization</h3>
        <p className="text-sm text-blue-700">
          {canBypassApproval ? (
            <>
              Documents approved through Document Control are automatically filed here. 
              As QA Manager, you can also upload documents directly using Single Upload or Bulk Upload.
            </>
          ) : canDownloadClean ? (
            <>
              Browse and download approved documents. As a Department Manager, your downloads are not watermarked.
              Use "Submit Document" to add new documents to the approval workflow.
            </>
          ) : (
            <>
              Browse and download approved documents from the BMS folders. 
              Downloaded documents will include an "UNCONTROLLED" watermark.
              Use "Submit Document" to add new documents to the approval workflow.
            </>
          )}
        </p>
      </div>

      <div className="flex gap-6">
        {/* Folder Tree Sidebar */}
        <div className="w-80 bg-white rounded-lg shadow border border-slate-200 p-4 h-fit sticky top-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800">Folders</h3>
            <button
              onClick={fetchDocuments}
              className="text-slate-400 hover:text-slate-600"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
          </div>

          {/* All Documents option */}
          <button
            onClick={() => { setSelectedFolder(null); setSelectedSubFolder(null); }}
            className={`w-full flex items-center px-3 py-2 rounded-lg mb-2 transition-colors ${
              !selectedFolder ? 'bg-blue-100 text-blue-800' : 'hover:bg-slate-100 text-slate-700'
            }`}
          >
            <FolderOpen size={18} className="mr-2" />
            <span className="flex-1 text-left text-sm font-medium">All Documents</span>
            <span className="text-xs bg-slate-200 px-2 py-0.5 rounded-full">{documents.length}</span>
          </button>

          <div className="border-t border-slate-200 my-3"></div>

          {/* Department Folders */}
          <div className="space-y-1 max-h-[60vh] overflow-y-auto">
            {departments.map(dept => (
              <div key={dept.id}>
                <button
                  onClick={() => toggleFolder(dept.id)}
                  className={`w-full flex items-center px-3 py-2 rounded-lg transition-colors ${
                    selectedFolder?.id === dept.id && !selectedSubFolder
                      ? 'bg-blue-100 text-blue-800'
                      : 'hover:bg-slate-100 text-slate-700'
                  }`}
                >
                  {expandedFolders[dept.id] ? (
                    <ChevronDown size={16} className="mr-1 text-slate-400" />
                  ) : (
                    <ChevronRight size={16} className="mr-1 text-slate-400" />
                  )}
                  {expandedFolders[dept.id] ? (
                    <FolderOpen size={18} className="mr-2 text-amber-500" />
                  ) : (
                    <FolderClosed size={18} className="mr-2 text-amber-500" />
                  )}
                  <span className="flex-1 text-left text-sm truncate" title={`${dept.id} ${dept.name}`}>
                    {dept.id} {dept.name}
                  </span>
                  <span className="text-xs bg-slate-200 px-2 py-0.5 rounded-full">
                    {getDocumentCount(dept.id)}
                  </span>
                </button>

                {/* Sub-folders */}
                {expandedFolders[dept.id] && (
                  <div className="ml-6 mt-1 space-y-1">
                    {/* Department root */}
                    <button
                      onClick={() => selectFolder(dept, null)}
                      className={`w-full flex items-center px-3 py-1.5 rounded text-sm transition-colors ${
                        selectedFolder?.id === dept.id && !selectedSubFolder
                          ? 'bg-blue-50 text-blue-700'
                          : 'hover:bg-slate-50 text-slate-600'
                      }`}
                    >
                      <FileText size={14} className="mr-2 text-slate-400" />
                      <span className="flex-1 text-left">All in {dept.id}</span>
                    </button>

                    {subFolders.map(sub => (
                      <button
                        key={sub.id}
                        onClick={() => selectFolder(dept, sub)}
                        className={`w-full flex items-center px-3 py-1.5 rounded text-sm transition-colors ${
                          selectedFolder?.id === dept.id && selectedSubFolder?.id === sub.id
                            ? 'bg-blue-50 text-blue-700'
                            : 'hover:bg-slate-50 text-slate-600'
                        }`}
                      >
                        <sub.icon size={14} className="mr-2 text-slate-400" />
                        <span className="flex-1 text-left">{sub.name}</span>
                        <span className="text-xs text-slate-400">
                          {getDocumentCount(dept.id, sub.id)}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Document List */}
        <div className="flex-1">
          {/* Search and Filter Bar */}
          <div className="bg-white rounded-lg shadow border border-slate-200 p-4 mb-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  type="text"
                  placeholder="Search documents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              <div className="text-sm text-slate-500">
                {filteredDocuments.length} document{filteredDocuments.length !== 1 ? 's' : ''}
                {selectedFolder && ` in ${selectedFolder.id} ${selectedFolder.name}`}
                {selectedSubFolder && ` / ${selectedSubFolder.name}`}
              </div>
            </div>
          </div>

          {/* Current Path Breadcrumb */}
          {selectedFolder && (
            <div className="bg-slate-50 rounded-lg px-4 py-2 mb-4 flex items-center text-sm">
              <button onClick={() => { setSelectedFolder(null); setSelectedSubFolder(null); }} className="text-blue-600 hover:underline">
                BMS Administration
              </button>
              <ChevronRight size={14} className="mx-2 text-slate-400" />
              <span className="font-medium text-slate-700">{selectedFolder.id} {selectedFolder.name}</span>
              {selectedSubFolder && (
                <>
                  <ChevronRight size={14} className="mx-2 text-slate-400" />
                  <span className="font-medium text-slate-700">{selectedSubFolder.name}</span>
                </>
              )}
            </div>
          )}

          {/* Documents Table */}
          <div className="bg-white rounded-lg shadow border border-slate-200 overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Document</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Version</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Effective Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredDocuments.map((doc, index) => (
                  <tr key={doc.id || index} className="hover:bg-slate-50">
                    <td className="px-4 py-4">
                      <div className="flex items-center">
                        <FileText className="text-blue-500 mr-3" size={20} />
                        <div>
                          <p className="font-medium text-slate-900">{doc.document_name}</p>
                          <p className="text-xs text-slate-500">{doc.description?.substring(0, 50) || 'No description'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-full capitalize">
                        {doc.document_category || doc.document_type || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-700">v{doc.version || '1.0'}</td>
                    <td className="px-4 py-4 text-sm text-slate-700">
                      {doc.effective_date ? new Date(doc.effective_date).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-4 py-4">
                      {doc.bms_direct_upload ? (
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full flex items-center w-fit">
                          <CheckCircle size={12} className="mr-1" />
                          Direct Upload
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full flex items-center w-fit">
                          <CheckCircle size={12} className="mr-1" />
                          Approved
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1">
                        {/* Preview */}
                        {isPreviewable(doc) && (
                          <button
                            onClick={() => openPreview(doc)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                            title="Preview"
                          >
                            <Eye size={16} />
                          </button>
                        )}
                        {/* Download */}
                        <button
                          onClick={() => handleDownload(doc)}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                          title="Download"
                        >
                          <Download size={16} />
                        </button>
                        {/* Revision History */}
                        <button
                          onClick={() => openRevisionHistory(doc)}
                          className="p-1.5 text-slate-600 hover:bg-slate-50 rounded"
                          title="Revision History"
                        >
                          <History size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredDocuments.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <FolderOpen className="mx-auto mb-4 text-slate-300" size={48} />
                <p className="font-medium">No documents found</p>
                <p className="text-sm mt-1">
                  {selectedFolder 
                    ? `No documents in ${selectedFolder.name}${selectedSubFolder ? ` / ${selectedSubFolder.name}` : ''}`
                    : 'Upload documents through Document Control or use Direct Upload'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Direct Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-slate-900 flex items-center">
                <Upload className="mr-2 text-green-600" size={24} />
                Direct Upload (Bypass Approval)
              </h2>
              <button onClick={() => setShowUploadModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> This uploads documents directly to BMS Administration without going through the approval workflow. 
                Use this for existing documents that are already in use and don't require approval.
              </p>
            </div>

            <form onSubmit={handleDirectUpload}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Select File *</label>
                  <input
                    type="file"
                    required
                    onChange={(e) => setUploadData({...uploadData, file: e.target.files[0]})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Document Name *</label>
                  <input
                    type="text"
                    required
                    value={uploadData.document_name}
                    onChange={(e) => setUploadData({...uploadData, document_name: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    placeholder="Enter document name"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Department *</label>
                    <select
                      required
                      value={uploadData.department}
                      onChange={(e) => setUploadData({...uploadData, department: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    >
                      <option value="">Select Department</option>
                      {departments.map(dept => (
                        <option key={dept.id} value={dept.name}>{dept.id} - {dept.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Category *</label>
                    <select
                      required
                      value={uploadData.document_category}
                      onChange={(e) => setUploadData({...uploadData, document_category: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    >
                      {subFolders.map(sub => (
                        <option key={sub.id} value={sub.id}>{sub.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Version</label>
                    <input
                      type="text"
                      value={uploadData.version}
                      onChange={(e) => setUploadData({...uploadData, version: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                      placeholder="1.0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Effective Date</label>
                    <input
                      type="date"
                      value={uploadData.effective_date}
                      onChange={(e) => setUploadData({...uploadData, effective_date: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea
                    value={uploadData.description}
                    onChange={(e) => setUploadData({...uploadData, description: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    placeholder="Enter document description..."
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => { setShowUploadModal(false); resetUploadForm(); }}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Upload to BMS
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900 flex items-center">
                <Eye className="mr-2 text-blue-600" size={20} />
                {selectedDocument.document_name}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDownload(selectedDocument)}
                  className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 flex items-center"
                >
                  <Download size={14} className="mr-1" />
                  Download
                </button>
                <button onClick={closePreview} className="text-slate-400 hover:text-slate-600">
                  <X size={24} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden bg-slate-100 min-h-[60vh] flex items-center justify-center">
              {previewLoading && (
                <div className="flex flex-col items-center justify-center text-slate-500">
                  <Loader2 className="w-8 h-8 animate-spin mb-2" />
                  <p>Loading preview...</p>
                </div>
              )}
              {previewError && (
                <div className="flex flex-col items-center justify-center text-red-500 p-4">
                  <AlertCircle className="w-12 h-12 mb-2" />
                  <p className="text-lg font-medium mb-2">Preview Not Available</p>
                  <p className="text-sm text-slate-500 text-center max-w-md">{previewError}</p>
                  <button
                    onClick={() => handleDownload(selectedDocument)}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
                  >
                    <Download size={16} className="mr-2" />
                    Download Instead
                  </button>
                </div>
              )}
              {previewBlobUrl && !previewLoading && !previewError && (
                <iframe
                  src={`${previewBlobUrl}#toolbar=0&navpanes=0`}
                  title="Document Preview"
                  type="application/pdf"
                  className="w-full h-full min-h-[60vh]"
                  style={{ border: 'none' }}
                >
                  <div className="flex flex-col items-center justify-center text-slate-500 p-4">
                    <FileText className="w-12 h-12 mb-2" />
                    <p>PDF preview not supported in this browser</p>
                    <button
                      onClick={() => handleDownload(selectedDocument)}
                      className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
                    >
                      <Download size={16} className="mr-2" />
                      Download PDF
                    </button>
                  </div>
                </iframe>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Revision History Modal */}
      {showRevisionModal && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-slate-900 flex items-center">
                <History className="mr-2 text-slate-600" size={24} />
                Revision History
              </h2>
              <button onClick={() => setShowRevisionModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>

            <div className="bg-slate-50 rounded-lg p-3 mb-4">
              <p className="font-medium text-slate-900">{selectedDocument.document_name}</p>
              <p className="text-sm text-slate-500">Current Version: v{selectedDocument.version || '1.0'}</p>
            </div>

            {revisionHistory.length > 0 ? (
              <div className="space-y-3">
                {revisionHistory.map((entry, index) => (
                  <div key={entry.id || index} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                        entry.action === 'uploaded' ? 'bg-blue-100 text-blue-800' :
                        entry.action === 'edited' ? 'bg-amber-100 text-amber-800' :
                        entry.action === 'approved' || entry.action === 'qa_approved' ? 'bg-green-100 text-green-800' :
                        entry.action === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-slate-100 text-slate-800'
                      }`}>
                        {entry.action?.replace(/_/g, ' ').toUpperCase()}
                      </span>
                      <span className="text-xs text-slate-500">
                        {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : 'N/A'}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700">
                      <span className="font-medium">{entry.employee_name}</span>
                    </p>
                    {entry.details && (
                      <p className="text-xs text-slate-500 mt-1">{entry.details}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <History className="mx-auto mb-2 text-slate-300" size={32} />
                <p>No revision history available</p>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowRevisionModal(false)}
                className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-slate-900 flex items-center">
                <Upload className="mr-2 text-blue-600" size={24} />
                Bulk Upload Documents
              </h2>
              <button onClick={() => setShowBulkUploadModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleBulkUpload} className="space-y-4">
              {/* Department Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Select Department *
                </label>
                <select
                  value={bulkUploadDept}
                  onChange={(e) => setBulkUploadDept(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select department...</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>
                      {dept.id} - {dept.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Category Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Document Category
                </label>
                <select
                  value={bulkUploadCategory}
                  onChange={(e) => setBulkUploadCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {subFolders.map(sf => (
                    <option key={sf.id} value={sf.id}>{sf.name}</option>
                  ))}
                </select>
              </div>

              {/* File Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Select Files *
                </label>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    multiple
                    onChange={(e) => setBulkFiles(Array.from(e.target.files))}
                    className="hidden"
                    id="bulk-file-input"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.md"
                  />
                  <label htmlFor="bulk-file-input" className="cursor-pointer">
                    <Upload className="mx-auto mb-2 text-slate-400" size={32} />
                    <p className="text-sm text-slate-600">
                      Click to select files or drag and drop
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      PDF, DOC, DOCX, XLS, XLSX, TXT, MD supported
                    </p>
                  </label>
                </div>
                {bulkFiles.length > 0 && (
                  <div className="mt-3 bg-slate-50 rounded-lg p-3">
                    <p className="text-sm font-medium text-slate-700 mb-2">
                      {bulkFiles.length} files selected:
                    </p>
                    <ul className="text-xs text-slate-600 space-y-1 max-h-32 overflow-y-auto">
                      {bulkFiles.map((file, idx) => (
                        <li key={idx} className="flex items-center">
                          <FileText size={12} className="mr-1 text-blue-500" />
                          {file.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Department Stats Preview */}
              {bulkUploadDept && (
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Current documents in {departments.find(d => d.id === bulkUploadDept)?.name}:</strong>{' '}
                    {deptStats.find(s => s.code === bulkUploadDept)?.total || 0}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowBulkUploadModal(false);
                    setBulkFiles([]);
                  }}
                  className="px-4 py-2 text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={bulkUploading || bulkFiles.length === 0 || !bulkUploadDept}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center"
                >
                  {bulkUploading ? (
                    <>
                      <RefreshCw className="animate-spin mr-2" size={16} />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2" size={16} />
                      Upload {bulkFiles.length} Files
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Submit Document Modal - For Document Control Workflow */}
      {showSubmitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-slate-800">Submit Document for Approval</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    Document will go through Department Manager and QA Manager approval
                  </p>
                </div>
                <button
                  onClick={() => setShowSubmitModal(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); handleSubmitDocument(); }} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Document Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={submitData.document_name}
                  onChange={(e) => setSubmitData({ ...submitData, document_name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  placeholder="Enter document name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Department (BMS Folder) <span className="text-red-500">*</span>
                </label>
                <select
                  value={submitData.department}
                  onChange={(e) => setSubmitData({ ...submitData, department: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  required
                >
                  <option value="">Select department</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>
                      {dept.id} - {dept.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Document Type
                </label>
                <select
                  value={submitData.document_type}
                  onChange={(e) => setSubmitData({ ...submitData, document_type: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                >
                  <option value="procedure">Procedure</option>
                  <option value="work_instruction">Work Instruction</option>
                  <option value="form">Form</option>
                  <option value="policy">Policy</option>
                  <option value="specification">Specification</option>
                  <option value="drawing">Drawing</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Description
                </label>
                <textarea
                  value={submitData.description}
                  onChange={(e) => setSubmitData({ ...submitData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  rows={3}
                  placeholder="Brief description of the document"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  File <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  onChange={(e) => setSubmitData({ ...submitData, file: e.target.files[0] })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">
                  Accepted formats: PDF, Word, Excel, PowerPoint
                </p>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Approval Workflow:</strong> Your document will be reviewed by the Department Manager, 
                  then by the QA Manager. You'll receive email notifications at each step.
                </p>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowSubmitModal(false)}
                  className="px-4 py-2 text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center"
                >
                  {submitting ? (
                    <>
                      <RefreshCw className="animate-spin mr-2" size={16} />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2" size={16} />
                      Submit for Approval
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BMSAdministration;
